import { memo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { CashSpecialIcon, Layout, PageTitle } from "lola-framework-ui-test";
import type { AxiosError } from "axios";
import { AuraLayout } from "../components/AuraLayout";
import { FlowHeroMark } from "../components/FlowHeroMark";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { APP_ROUTES, TITLE_COLOR } from "../constants";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useSendStore } from "../store/useSendStore";
import { useComposeStore } from "../store/useComposeStore";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import {
  createOperation,
  getAllPaymentMethods,
  getQuote,
  getWidgetApiError,
  isDeadLinkError,
} from "../services/api";
import type { ICreateOperationRequest } from "../services/api";
import {
  fallbackCanonicalName,
  type CanonicalName,
} from "../api/parse";
import { formatCanonicalName } from "../api/recipients";
import { PayoutMethodType, type RecipientInput } from "../interfaces/payout";
import { FUNDING_METHOD_CATEGORY } from "../utils/catalog";
import {
  createWithFreshQuote,
  waitForPersistHydration,
} from "../utils/quoteCommit";
import {
  resolvePayoutPaymentMethodId,
  sanitizePayoutAfterRegister,
} from "../utils/registerPayoutInstrument";
import { getEvents, safeCapture } from "../analytics";
import {
  clearFirstSendWarningCache,
  wasFirstSendWarningShown,
} from "../hooks/useFirstSendWarning";
import { clearPhoneSkip } from "../utils/phoneGate";
import {
  forgetAuthorizeCvv,
  peekAuthorizeCvv,
} from "../utils/fresh-cvv-alias";

/**
 * Dead-link errors (LINK_NOT_FOUND / 404, expired, revoked) are handled
 * globally by the dead-link event handler, so we must not override them here.
 */
const isSessionExpiredError = (err: unknown): boolean => {
  const axiosError = err as AxiosError<{ error?: { code?: string } }>;
  const code = axiosError?.response?.data?.error?.code;
  return (
    isDeadLinkError(err) ||
    code === "LINK_NOT_FOUND" ||
    axiosError?.response?.status === 404
  );
};

/**
 * Resolve structured recipient name from the draft (new shape or legacy
 * firstName/lastName drafts persisted before parse/name parity).
 */
const resolveRecipientCanonicalName = (
  recipient: RecipientInput
): CanonicalName | null => {
  if (recipient.canonicalName?.firstName && recipient.canonicalName?.lastName) {
    return recipient.canonicalName;
  }
  if (recipient.firstName?.trim() && recipient.lastName?.trim()) {
    return {
      firstName: recipient.firstName.trim(),
      lastName: recipient.lastName.trim(),
    };
  }
  if (recipient.fullName?.trim()) {
    return fallbackCanonicalName(recipient.fullName);
  }
  return null;
};

const buildRecipientParty = (
  draft: NonNullable<ReturnType<typeof useSendStore.getState>["draft"]>
): ICreateOperationRequest["recipient"] => {
  const name = resolveRecipientCanonicalName(draft.recipient);
  if (!name) return undefined;

  const destCountry = draft.destinationCountry?.trim().toUpperCase() || null;
  const bareCashState =
    draft.payoutMethod === PayoutMethodType.CASH_PICKUP
      ? draft.payout.stateCode?.trim()
      : null;
  const recipientState = (() => {
    if (!bareCashState || !destCountry) return undefined;
    const upper = bareCashState.toUpperCase();
    if (upper.includes("-")) return upper;
    return `${destCountry}-${upper}`;
  })();
  const address =
    destCountry || recipientState
      ? {
          ...(destCountry ? { countryISO2: destCountry } : {}),
          ...(recipientState ? { state: recipientState } : {}),
        }
      : undefined;

  const walletPhone =
    draft.payoutMethod === PayoutMethodType.MOBILE_WALLET
      ? draft.payout.phone?.trim()
      : undefined;
  const phone = walletPhone || draft.recipient.phone?.trim() || undefined;

  const dateOfBirth = draft.recipient.dateOfBirth?.trim() || undefined;

  return {
    name: {
      firstName: name.firstName,
      lastName: name.lastName,
      ...(name.middleName ? { middleName: name.middleName } : {}),
      ...(name.secondLastName ? { secondLastName: name.secondLastName } : {}),
    },
    ...(address ? { address } : {}),
    ...(phone ? { phone } : {}),
    ...(dateOfBirth ? { dateOfBirth } : {}),
  };
};

/**
 * Module-level guard: survives StrictMode / route re-mounts so the operation is
 * created exactly once. Reset on completion so retries work.
 */
let isOperationInFlight = false;

const MIN_TRANSFER_VISIBLE_MS = 2000;

const holdMinimumVisible = (visibleSince: number) => {
  const remaining = MIN_TRANSFER_VISIBLE_MS - (Date.now() - visibleSince);
  if (remaining <= 0) return Promise.resolve();
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, remaining);
  });
};

/**
 * "Sending your money" screen. Creates the operation from the signed quote
 * stored at confirm (CIP can outlive a JWT — refresh if stale, then create).
 * Seeds the receipt with the create response so live tracking starts from the
 * latest known status.
 */
export const SendingMoneyPage = memo(() => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const { session } = useSessionStore();
  const { redirectTo } = useRouteWithToken();

  const colorConfig = theme.whiteView;
  const { dropzoneColors, backgroundIcon } = colorConfig;
  const visibleSinceRef = useRef(Date.now());

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("committing"));

    if (isOperationInFlight) return;
    isOperationInFlight = true;

    const run = async () => {
      try {
        await waitForPersistHydration(useSendStore.persist);
        const draft = useSendStore.getState().draft;
        const capturedQuoteState = useSendStore.getState().quoteState;

        const linkId = session.data?.linkId;
        const userStatus = session.data?.userStatus;
        const canSend = !userStatus?.blocked && !userStatus?.suspended;
        if (!canSend) {
          redirectTo(APP_ROUTES.HOME_PAGE, { replace: true });
          return;
        }
        const capabilityKey =
          draft?.capabilityKey ?? capturedQuoteState?.capabilityKey ?? "";
        const signedQuote = capturedQuoteState?.signedQuote;
        if (!linkId || !draft || !capabilityKey || !signedQuote) {
          redirectTo(APP_ROUTES.TRANSFER_PAGE, { replace: true });
          return;
        }

        const resolveFundingPaymentMethodId = async (): Promise<
          string | undefined
        > => {
          const category = FUNDING_METHOD_CATEGORY[draft.fundingMethod];
          if (category === "CASH") return undefined;
          if (draft.fundingPaymentMethodId) return draft.fundingPaymentMethodId;

          if (category === "ACCOUNT") {
            try {
              const pm = await getAllPaymentMethods(linkId);
              const ba =
                pm.bankAccounts.find((b) => b.isDefault && b.enabled) ||
                pm.bankAccounts.find((b) => b.enabled);
              return ba?.paymentMethodId;
            } catch (e) {
              console.error(
                "[SendingMoneyPage] payment methods lookup failed",
                e,
              );
            }
          }
          return undefined;
        };

        const fundingPaymentMethodId = await resolveFundingPaymentMethodId();
        const category = FUNDING_METHOD_CATEGORY[draft.fundingMethod];
        if (category !== "CASH" && !fundingPaymentMethodId) {
          throw new Error("No funding payment method selected");
        }

        const cardholderName =
          resolveRecipientCanonicalName(draft.recipient)
            ? formatCanonicalName(
                resolveRecipientCanonicalName(draft.recipient)!
              )
            : draft.recipient.fullName;

        let payoutPaymentMethodId: string | undefined;
        if (
          draft.payoutMethod === PayoutMethodType.PUSH_TO_CARD &&
          draft.payout.paymentMethodId
        ) {
          payoutPaymentMethodId = draft.payout.paymentMethodId;
        } else if (
          draft.payoutMethod === PayoutMethodType.BANK_ACCOUNT &&
          draft.payout.paymentMethodId &&
          draft.payout.source === "saved"
        ) {
          payoutPaymentMethodId = draft.payout.paymentMethodId;
        } else if (
          draft.payoutMethod === PayoutMethodType.PUSH_TO_CARD ||
          draft.payoutMethod === PayoutMethodType.BANK_ACCOUNT
        ) {
          payoutPaymentMethodId = await resolvePayoutPaymentMethodId(
            linkId,
            draft.payoutMethod,
            draft.payout,
            draft.destinationCountry,
            cardholderName
          );
        }

        // Hygiene: once registered, drop raw PAN/CLABE from persisted draft.
        if (
          payoutPaymentMethodId &&
          (draft.payout.cardPan || draft.payout.accountNumber)
        ) {
          useSendStore.getState().setDraft({
            ...draft,
            payout: sanitizePayoutAfterRegister(
              draft.payout,
              payoutPaymentMethodId
            ),
          });
        }

        const quoteForCommit = {
          ...capturedQuoteState,
          destinationCurrency:
            capturedQuoteState.destinationCurrency || draft.destinationCurrency,
          fundingMethod:
            capturedQuoteState.fundingMethod ?? draft.fundingMethod,
          payoutMethod: capturedQuoteState.payoutMethod ?? draft.payoutMethod,
          capabilityKey,
          ...(capturedQuoteState.cardId
            ? { cardId: capturedQuoteState.cardId }
            : {}),
        };

        const fundingCvvToken =
          category === "CARD" && fundingPaymentMethodId
            ? peekAuthorizeCvv(fundingPaymentMethodId)
            : undefined;

        const { result: operation, quote: usedQuote } =
          await createWithFreshQuote({
            quote: quoteForCommit,
            route: {
              sourceCountry: draft.sourceCountry,
              destinationCountry: draft.destinationCountry,
              sourceCurrency: draft.sourceCurrency,
            },
            getQuote,
            create: (freshSignedQuote) =>
              createOperation(linkId, {
                signedQuote: freshSignedQuote,
                ...(fundingPaymentMethodId
                  ? { fundingPaymentMethodId }
                  : {}),
                ...(fundingCvvToken ? { fundingCvvToken } : {}),
                ...(payoutPaymentMethodId
                  ? { payoutPaymentMethodId }
                  : {}),
                recipient: buildRecipientParty(draft),
              }),
          });
        useSendStore.getState().setQuoteState(usedQuote);
        if (fundingCvvToken && fundingPaymentMethodId) {
          forgetAuthorizeCvv(fundingPaymentMethodId);
        }

        safeCapture(getEvents().OPERATION_CREATED, {
          success: true,
          first_send_warning_shown: wasFirstSendWarningShown(),
          operation_id: operation.operationId,
          quote_id: operation.quoteId,
          status: operation.status,
          send_amount: operation.sendAmount,
          receive_amount: operation.receiveAmount,
          exchange_rate: operation.exchangeRate,
          total_cost: operation.totalCost,
          funding_payment_method_id: fundingPaymentMethodId,
          payout_payment_method_id: payoutPaymentMethodId,
          funding_method: draft.fundingMethod,
          payout_method: draft.payoutMethod,
          capability_key: capabilityKey,
        });

        clearFirstSendWarningCache();
        if (linkId) clearPhoneSkip(linkId);
        useComposeStore.getState().resetCompose();
        useStore.getState().setAmountState({ amount: "", anchor: "send" });

        await holdMinimumVisible(visibleSinceRef.current);
        if (category === "CASH") {
          redirectTo(APP_ROUTES.HOW_TO_DEPOSIT_CASH_PAGE, { replace: true });
          return;
        }
        redirectTo(
          APP_ROUTES.RECEIPT_SUMMARY_PAGE.replace(
            ":operationId",
            operation.operationId
          ),
          { replace: true, state: { operation } }
        );
      } catch (err) {
        if (isSessionExpiredError(err)) return;
        const apiCode = getWidgetApiError(err)?.code;
        if (apiCode === "USER_BLOCKED" || apiCode === "USER_SUSPENDED") {
          redirectTo(APP_ROUTES.ERROR_PAGE, {
            replace: true,
            state: {
              title: t("error.sendsDisabledTitle"),
              subTitle: null,
              message: t("error.sendsDisabledMessage"),
              btnText: t("common.needHelp"),
              isOut: false,
              pageUrl: APP_ROUTES.SUPPORT_PAGE,
            },
          });
          return;
        }
        console.error("[SendingMoneyPage] create failed", err);
        safeCapture(getEvents().OPERATION_CREATED, {
          success: false,
          first_send_warning_shown: wasFirstSendWarningShown(),
          error: err instanceof Error ? err.message : "Create operation failed",
          error_code: "OPERATION_CREATE_FAILED",
        });
        clearFirstSendWarningCache();
        if (session.data?.linkId) clearPhoneSkip(session.data.linkId);
        redirectTo(APP_ROUTES.SERVICE_UNAVAILABLE_PAGE, { replace: true });
      } finally {
        isOperationInFlight = false;
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuraLayout colorConfig={colorConfig} className="flow-landing-page">
      <Layout.Content className="landing-section flow-hero-section">
        <ScrollFadeIndicator color={colorConfig.bodyCopy} />
        <section className="flow-hero">
          <FlowHeroMark
            showSpinner
            dropzoneColors={dropzoneColors}
            backgroundIcon={backgroundIcon}
          >
            <CashSpecialIcon size={60} colors={dropzoneColors} />
          </FlowHeroMark>
          <section className="landing-container">
            <PageTitle
              highlight={t("sending.title")}
              highlightColor={TITLE_COLOR}
              secudnary={t("sending.subtitle")}
              secudnaryColor={TITLE_COLOR}
            />
          </section>
        </section>
      </Layout.Content>
    </AuraLayout>
  );
});
